import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { patchMe } from '../api/portal';
import { ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth';

export function AccountPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const [company, setCompany] = useState(user?.company_name ?? '');
  const [email, setEmail] = useState(user?.contact_email ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwOpen, setPwOpen] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCompany(user?.company_name ?? '');
    setEmail(user?.contact_email ?? '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirm('');
    setPwOpen(false);
    setOk(null);
    setErr(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, user?.company_name, user?.contact_email]);

  const emailChanged = email.trim().toLowerCase() !== (user?.contact_email ?? '').toLowerCase();

  const mut = useMutation({
    mutationFn: () => {
      if (newPassword && newPassword !== confirm) {
        throw new ApiError(400, 'mismatch', 'New passwords do not match.');
      }
      return patchMe({
        company_name: company.trim(),
        contact_email: emailChanged ? email.trim() : undefined,
        current_password: currentPassword || undefined,
        new_password: newPassword || undefined,
      });
    },
    onSuccess: (next) => {
      updateUser({
        id: next.id,
        company_name: next.company_name,
        contact_email: next.contact_email,
        tier: next.tier,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      setPwOpen(false);
      setErr(null);
      setOk('Saved.');
    },
    onError: (e) => {
      setOk(null);
      setErr(e instanceof ApiError ? e.message : String(e));
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" onMouseDown={onClose}>
      <div
        className="absolute bottom-16 left-3 w-[min(340px,calc(100vw-1.5rem))]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <form
          className="card space-y-3 p-4"
          onSubmit={(e: FormEvent) => { e.preventDefault(); setOk(null); mut.mutate(); }}
        >
          <div>
            <div className="text-[13px] font-semibold text-white">Account</div>
            <p className="text-[12px] text-subtle">Company and how you sign in.</p>
          </div>
          <div>
            <label className="label">Company</label>
            <input className="input" value={company} onChange={(e) => setCompany(e.target.value)} required />
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          {(pwOpen || emailChanged) && (
            <div>
              <label className="label">Current password</label>
              <input type="password" className="input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" />
              {emailChanged && !pwOpen && (
                <p className="mt-1 text-[12px] text-subtle">Needed to change email.</p>
              )}
            </div>
          )}
          {pwOpen && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">New</label>
                <input type="password" className="input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
              </div>
              <div>
                <label className="label">Confirm</label>
                <input type="password" className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
              </div>
            </div>
          )}
          {err && <div className="alert-bad">{err}</div>}
          {ok && <div className="alert-ok">{ok}</div>}
          <div className="flex items-center justify-between gap-2">
            <button type="button" className="text-[12px] text-subtle hover:text-white" onClick={() => setPwOpen((v) => !v)}>
              {pwOpen ? 'Hide password' : 'Change password'}
            </button>
            <button type="submit" className="btn-primary" disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
